import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";
import {
  buildClientDetailsHref,
} from "Helpers/customersBreadcrumbs";
import { pickClientDisplayName } from "Helpers/pickProfileEntityTitle";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ClientProfileService } from "subModule/src/core/services";

function useFrozenEntityTitleByType(
  typeName: "clients" | "customers",
  entityId?: string,
): string {
  const params = useParams();
  const id = entityId ?? params.id;
  const [entityTitle, setEntityTitle] = useState("");

  useEffect(() => {
    if (!id) return;
    let active = true;
    ClientProfileService.getClientByID(+id, typeName)
      .then((res) => {
        if (!active) return;
        const name = pickClientDisplayName(res);
        if (name) setEntityTitle(name);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [id, typeName]);

  return entityTitle;
}

export function useFrozenClientEntityTitle(clientId?: string): string {
  return useFrozenEntityTitleByType("clients", clientId);
}

export function useFrozenCustomerEntityTitle(customerId?: string): string {
  return useFrozenEntityTitleByType("customers", customerId);
}

export function useFrozenClientTabBreadcrumbs(
  actionLabelKey: string,
): BreadcrumbItem[] {
  const { id, accountId, invoicingPreference } = useParams();
  const entityTitle = useFrozenClientEntityTitle();

  return useMemo(
    () => [
      { labelKey: "customers" },
      { labelKey: "customersList", href: "/clients" },
      {
        labelKey: "name",
        ...(entityTitle ? { label: entityTitle } : {}),
        href: buildClientDetailsHref({ id, accountId, invoicingPreference }),
      },
      { labelKey: actionLabelKey },
    ],
    [id, accountId, invoicingPreference, entityTitle, actionLabelKey],
  );
}

/** Individual customer nested tabs — Customers > List > Name > Tab */
export function useFrozenCustomerTabBreadcrumbs(
  actionLabelKey: string,
): BreadcrumbItem[] {
  const { id } = useParams();
  const entityTitle = useFrozenCustomerEntityTitle();

  return useMemo(
    () => [
      { labelKey: "customers" },
      { labelKey: "customersList", href: "/clients" },
      {
        labelKey: "name",
        ...(entityTitle ? { label: entityTitle } : {}),
        href: id ? `/customer/${id}` : undefined,
      },
      { labelKey: actionLabelKey },
    ],
    [id, entityTitle, actionLabelKey],
  );
}
